/**
 * What an operator may launch at this counter, and which reader it will use.
 *
 * The Clients page now has two ways to read the same person's document — the chip, and the optical
 * reader — and they fail differently: the chip needs a card the workstation can talk to, the optical
 * path needs a vendor runtime, a scanner, and often a choice between two of them. Putting those
 * rules here rather than in a component keeps the decision testable and keeps the two families from
 * growing separate ideas of "ready".
 *
 * Nothing in this module talks to anything. Every function is a fold over state somebody else
 * fetched.
 */

import {
    canScanDocuments,
    type DetectedDocumentReader,
    type DocumentReaderState,
} from './documentScan';
import {
    middlewareBlocksRead,
    middlewareMessageKey,
    type EidMiddlewareReadiness,
} from './eidMiddleware';
import {
    canReadEid,
    type ChipReaderListing,
    type ChipReaderState,
    type DetectedChipReader,
} from './eidReader';
import type { ReaderIndicatorLevel } from './idCardReader';
import type { HardwareServiceStatus } from './labelPrinting';

/** The two ways this application can read an identity document. */
export type IdentityProvider = 'chip' | 'optical';

/** Whether one provider can be launched here, and what to tell the operator either way. */
export interface IdentityProviderAvailability {
    readonly provider: IdentityProvider;
    readonly available: boolean;
    readonly level: ReaderIndicatorLevel;
    /** An i18n key. Never a sentence: this module does not know what language a counter runs in. */
    readonly messageKey: string;
}

/**
 * The little both providers' device records have in common.
 *
 * Enough to choose a reader and to name one on screen, and deliberately nothing else: the chip and
 * the optical families each carry fields the other has no use for, and a shared record that grew to
 * hold both would be the first place the two started agreeing about things they should not.
 */
export interface SelectableReader {
    readonly id: string;
    readonly displayName: string;
    readonly serialSuffix: string | null;
    readonly connected: boolean;
}

/** Which reader an operation would name, and whether a person still has to say. */
export interface ReaderChoice<TReader extends SelectableReader> {
    readonly readers: readonly TReader[];
    /**
     * The device id an operation should carry, or null while that is not yet decidable.
     *
     * Never a guess: with two readers attached and nothing chosen this is null and
     * {@link choiceRequired} is true, because picking one for somebody would mean an operation
     * starting on a reader they are not standing at.
     */
    readonly deviceId: string | null;
    readonly choiceRequired: boolean;
    /**
     * Whether this workstation recommends a reader that is not currently attached.
     *
     * Worth surfacing rather than silently ignoring: it usually means a reader was unplugged or
     * moved, and an operator who chose one last week deserves to know why they are being asked
     * again.
     */
    readonly preferenceStale: boolean;
}

/** Which optical reader a scan would name. */
export type OpticalDeviceChoice = ReaderChoice<DetectedDocumentReader>;

/** Which chip reader a read would use. */
export type ChipDeviceChoice = ReaderChoice<DetectedChipReader>;

const CHIP_READY = 'identityReader.chip.ready';
const OPTICAL_READY = 'identityReader.optical.ready';

/**
 * Whether the chip path can be launched.
 *
 * Deliberately says nothing about whether a *card* is in the slot: presence cannot be known without
 * connecting to the card, and connecting to check is what once stole the card from a read in
 * progress. That reasoning covers the card and only the card. Whether a *reader* is attached is a
 * different question with a cheap answer — PC/SC enumerates readers without connecting to anything,
 * which is exactly what `GET /v1/eid/readers` describes — so the inventory is consulted here rather
 * than left to fail at the point of use.
 *
 * The honest question this answers is therefore "could this workstation read a card now", and the
 * modal still asks for the card.
 *
 * Three inputs, resolved in this order: what the service says about itself, whether this build
 * implements chip reading at all, whether the national middleware can serve a read, and only then
 * what is physically attached. Middleware comes before hardware deliberately — the fix for a
 * stopped middleware is an application, not a socket, and an operator told the wrong one looks in
 * the wrong place.
 */
export function deriveChipAvailability(
    status: HardwareServiceStatus | null,
    readers: ChipReaderListing | null,
    middleware: EidMiddlewareReadiness | null = null,
): IdentityProviderAvailability {
    if (status === null) {
        return checking('chip');
    }

    const unusable = describeService(status);

    if (unusable !== null) {
        return { provider: 'chip', available: false, ...unusable };
    }

    // A capability, not a device: this says the build implements chip reading, which is why it is
    // asked before the inventory rather than instead of it.
    if (!canReadEid(status)) {
        return refused('chip', 'identityReader.chip.noReaderSupport');
    }

    // Third, and before the inventory: a reader plugged into a workstation whose middleware is
    // stopped is still useless, and saying "no reader" there would send an operator to the socket
    // instead of to the application they need to start. Only a positive `unavailable` refuses;
    // `unknown`, `not-required` and a service too old to report at all fall through untouched.
    if (middlewareBlocksRead(middleware)) {
        return refused('chip', middlewareMessageKey(middleware!));
    }

    if (readers === null) {
        return {
            provider: 'chip',
            available: false,
            level: 'warning',
            messageKey: 'identityReader.chip.checking',
        };
    }

    if (!readers.ok) {
        // Fails closed, and says so in its own words. An inventory nobody could read is not
        // evidence of a reader, and the alternative — assuming one — costs an operator a card
        // cycle and a failure at a counter, where this costs one reconcile.
        return refused('chip', 'identityReader.chip.unknown');
    }

    if (!readers.value.readers.some(reader => reader.connected)) {
        return refused('chip', 'identityReader.chip.noReader');
    }

    // Ready here means "a read could be started", not "a read needs nothing further": a workstation
    // with two readers is ready and still has to be told which one.
    return { provider: 'chip', available: true, level: 'success', messageKey: CHIP_READY };
}

/**
 * Whether the optical path can be launched.
 *
 * Three separate things have to hold, and they are reported separately because the fix differs:
 * this build has to support scanning at all, the vendor runtime has to be installed and approved,
 * and a reader has to be attached. A workstation that fails the first one is not a workstation with
 * an unplugged scanner.
 */
export function deriveOpticalAvailability(
    status: HardwareServiceStatus | null,
    readers: DocumentReaderState | null,
): IdentityProviderAvailability {
    if (status === null) {
        return checking('optical');
    }

    const unusable = describeService(status);

    if (unusable !== null) {
        return { provider: 'optical', available: false, ...unusable };
    }

    if (!canScanDocuments(status)) {
        return {
            provider: 'optical',
            available: false,
            level: 'error',
            messageKey: 'identityReader.optical.notSupported',
        };
    }

    if (readers === null) {
        return {
            provider: 'optical',
            available: false,
            level: 'warning',
            messageKey: 'identityReader.optical.checking',
        };
    }

    const sdk = describeRuntime(readers);

    if (sdk !== null) {
        return { provider: 'optical', available: false, ...sdk };
    }

    if (readers.readers.length === 0) {
        return {
            provider: 'optical',
            available: false,
            level: 'error',
            messageKey: 'identityReader.optical.noReader',
        };
    }

    // Ready here means "a scan could be started", not "a scan needs nothing further": a workstation
    // with two readers is ready and still has to be told which one.
    return { provider: 'optical', available: true, level: 'success', messageKey: OPTICAL_READY };
}

/** A provider that cannot be launched, and the one thing worth telling the operator about it. */
function refused(provider: IdentityProvider, messageKey: string): IdentityProviderAvailability {
    return { provider, available: false, level: 'error', messageKey };
}

/** Neither provider can be judged before the service has answered once. */
function checking(provider: IdentityProvider): IdentityProviderAvailability {
    return {
        provider,
        available: false,
        level: 'warning',
        messageKey: 'identityReader.service.checking',
    };
}

/** The reason a service is unusable for either provider, or null when it is usable. */
function describeService(
    status: HardwareServiceStatus,
): Pick<IdentityProviderAvailability, 'level' | 'messageKey'> | null {
    switch (status.state) {
        case 'unavailable':
            return { level: 'error', messageKey: 'identityReader.service.stopped' };
        case 'unrecognised':
            return { level: 'error', messageKey: 'identityReader.service.unrecognised' };
        case 'incompatible':
            return { level: 'error', messageKey: 'identityReader.service.incompatible' };
        case 'unpaired':
            // An unconnected workstation reports nothing about its hardware, so there is no
            // capability list to consult and nothing to say beyond "connect this workstation".
            return { level: 'error', messageKey: 'identityReader.service.notPaired' };
        default:
            return null;
    }
}

/** The reason the vendor runtime blocks scanning, or null when it does not. */
function describeRuntime(
    readers: DocumentReaderState,
): Pick<IdentityProviderAvailability, 'level' | 'messageKey'> | null {
    switch (readers.sdk) {
        case 'not-installed':
            return { level: 'error', messageKey: 'identityReader.optical.runtimeMissing' };
        case 'unsupported':
            return { level: 'error', messageKey: 'identityReader.optical.runtimeUnsupported' };
        case 'unknown':
            return { level: 'warning', messageKey: 'identityReader.optical.runtimeUnknown' };
        default:
            return null;
    }
}

/**
 * Works out which reader an operation would use.
 *
 * The order is deliberate. What the operator picked in this session wins, because they are the one
 * standing at the counter. Then what the service itself resolved, which is the same strict rule an
 * operation applies and is null whenever that is ambiguous. Only then the stored recommendation, and
 * only if the reader it names is actually attached. Nothing here ever promotes a preference into an
 * answer while two readers are plugged in — that is the rule the whole selection model rests on.
 *
 * Shared by both providers rather than written twice: the rule is about how a person chooses
 * hardware, not about what the hardware is, and two copies of it would eventually disagree.
 */
function chooseDevice<TReader extends SelectableReader>(
    state: {
        readers: TReader[];
        selectedDeviceId: string | null;
        preferredDeviceId: string | null;
    } | null,
    chosen: string | null,
): ReaderChoice<TReader> {
    if (state === null) {
        return { readers: [], deviceId: null, choiceRequired: false, preferenceStale: false };
    }

    const usable = state.readers.filter(reader => reader.connected);
    const attached = (id: string | null) =>
        id !== null && usable.some(reader => reader.id === id) ? id : null;

    const preferenceStale =
        state.preferredDeviceId !== null && attached(state.preferredDeviceId) === null;

    const deviceId =
        attached(chosen) ??
        attached(state.selectedDeviceId) ??
        attached(state.preferredDeviceId) ??
        (usable.length === 1 ? usable[0].id : null);

    return {
        readers: state.readers,
        deviceId,
        choiceRequired: deviceId === null && usable.length > 1,
        preferenceStale,
    };
}

/** Which optical reader a scan would name, and whether a person still has to say. */
export function chooseOpticalDevice(
    state: DocumentReaderState | null,
    chosen: string | null,
): OpticalDeviceChoice {
    return chooseDevice(state, chosen);
}

/**
 * Which chip reader a read would use, and whether a person still has to say.
 *
 * The same rule as the optical one and, unlike it, one whose answer the modal cannot carry: a read
 * names no device, so on a two-reader counter the choice has to reach the *service* before a read
 * can succeed. That is why the launcher records a chip choice rather than holding it in the page.
 */
export function chooseChipDevice(
    state: ChipReaderState | null,
    chosen: string | null,
): ChipDeviceChoice {
    return chooseDevice(state, chosen);
}

/**
 * The provider a launcher opens on.
 *
 * The chip when it can be used, because it is the one that produces an authenticated result. The
 * optical reader is the fallback for a document whose chip will not answer, which is the situation
 * it was bought for.
 */
export function defaultIdentityProvider(
    chip: IdentityProviderAvailability,
    optical: IdentityProviderAvailability,
): IdentityProvider | null {
    if (chip.available) {
        return 'chip';
    }

    return optical.available ? 'optical' : null;
}

/**
 * How one reader is named on screen.
 *
 * The last few characters of a serial, never the whole one: two identical scanners on one counter
 * have to be tellable apart, and that is all a suffix is for. The service never sends more than
 * four, so there is nothing here to truncate — this only decides whether to show them.
 */
export function describeReader(reader: SelectableReader): string {
    return reader.serialSuffix === null || reader.serialSuffix.length === 0
        ? reader.displayName
        : `${reader.displayName} (…${reader.serialSuffix})`;
}
